import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError, of } from "rxjs";
import { catchError, map, share, delay } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { environment } from "../../environments";
import { Filter, User } from "../interfaces/user";
import { Tier } from "../interfaces/v2/tier";
import { ListUserResponse, Profile, UserInfoData } from "../interfaces/v2/user";
import { AuthService } from "./auth.service";

/** Job kind for POST run-audit-for-tier-owner; must match GET /background-job/:kind/:job_id */
export const JOB_KIND_SHARKSCOPE_TIER_OWNER_AUDIT = "sharkscope_tier_owner_audit";

/** Poll GET `/v2/misc/background-job/:kind/:job_id` */
export interface BackgroundJobStatus {
  kind: string;
  status: string;
  phase: string;
  percent: number;
  processed: number;
  total: number;
  message?: string;
  error?: string;
  started_at: string;
  updated_at: string;
  extra?: Record<string, unknown>;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
    private isTierListResponse(value: any): value is { tiers: Tier[]; freeAvailable: boolean } {
      return value != null && Array.isArray(value.tiers) && typeof value.freeAvailable === "boolean";
    }
  baseURLv2 = environment.API_URL + "v2/user/";
  baseURLmisc =  environment.API_URL + "v2/misc/"

  private static lastRequestTime = 0;
  private static lastRequest: Observable<User> | null = null;

  private lastUser: User | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {}

  getCurrentUser(): Observable<User> {
    const now = Date.now();
    if (now - UserService.lastRequestTime < 9000 && UserService.lastRequest != null) {
      return UserService.lastRequest;
    }
    UserService.lastRequestTime = now;
    const req = this.http.get<User>(this.baseURLv2 + "full-data").pipe(
      map((user) => {
        this.lastUser = user;
        return user;
      }),
      catchError((error: any) => {
        console.error("[USER SERVICE ERROR] Failed to get current user", error);
        // Clear cache on error to prevent returning stale values
        this.lastUser = null;
        UserService.lastRequest = null;
        UserService.lastRequestTime = 0;
        return throwError(error);
      }),
      share()
    );
    UserService.lastRequest = req;
    return req;
  }

  getPendingRequest(): Observable<boolean> {
    return this.http.get<string>(this.baseURLv2 + "requests").pipe(
      withErrorHandler({ operation: 'getPendingRequest', cache: true, fallback: 'false', timeoutMs: 30000, retries: 2 }),
      map((e) => {
        return e.toString() == "true";
      })
    );
  }

  getRole() {
    return this.http.get<{ sa: boolean; ta: boolean; master: boolean }>(
      this.baseURLv2 + "role"
    ).pipe(
      withErrorHandler({ operation: 'getRole', cache: true, fallback: { sa: false, ta: false, master: false }, timeoutMs: 30000, retries: 2 })
    );
  }

  async getLastUser(): Promise<User> {
    if (this.lastUser != null) {
      return this.lastUser;
    }
    if (!this.authService.token) {
      return null;
    }
    const user = await this.getCurrentUser().toPromise();
    return user;
  }

  searchUsers(name: string, page: number = 0) {
    return this.http.get<ListUserResponse>(this.baseURLv2 + "search", {
      params: {
        name,
        page: page.toString(),
      },
    }).pipe(
      withErrorHandler({
        operation: 'searchUsers',
        cache: true,
        fallback: {
        users: [],
        totalCount: 0,
        pageCount: 0,
        page: 0,
        paging: { page: 0, per_page: 0, total: 0 },
        data: []
        },
        timeoutMs: 30000,
        retries: 2
      })
    );
  }

  getFilters() {
    return this.http.get<
      {
        deleted: boolean;
        id: number;
        data: Filter;
        auto_schedule: boolean;
        priority: number;
        reg_time: number | null;
        use_reg_time: boolean;
        user_id: number;
        user_name: string;
      }[]
    >(this.baseURLv2 + "filters").pipe(
      withErrorHandler({ operation: 'getFilters', cache: true, fallback: [], timeoutMs: 30000, retries: 2 })
    );
  }

  getFiltersFromTier(get_tier: boolean = false, all_tiers: boolean = false) {
    return this.http.post<
      {
        deleted: boolean;
        id: number;
        data: Filter;
        auto_schedule: boolean;
        priority: number;
        reg_time: number | null;
        use_reg_time: boolean;
        user_id: number;
        user_name: string;
        tier_id: number;
      }[]
    >(this.baseURLv2 + "filters", { get_tier, all_tiers }).pipe(
        withErrorHandler({
          operation: 'getFiltersFromTier',
          cache: true,
          fallback: [],
          timeoutMs: 30000,
          retries: 2
        })
    );
  }

  copyFilters(filters: Array<number>, tiers: Array<number>, new_names: Array<string> | null = null) {
    return this.http.post<{ id: number }>(
      this.baseURLv2 + "filters/copy-filters",
      {
        filters,
        tiers,
        new_names
      }
    ).pipe(
      withErrorHandler({ operation: 'copyFilters', timeoutMs: 60000, retries: 1 }),
      catchError(err => {
        console.error('copyFilters failed:', err);
        return throwError(() => err);
      })
    );
  }

  // Omit reg_time / use_reg_time to leave the filter default unchanged.
  // Send use_reg_time: true with a unix or null to write or clear it.
  updateUserFilter(
    filters: Array<{
      id: number;
      data: object;
      auto_schedule: boolean;
      priority: number;
      reg_time?: number | null;
      use_reg_time?: boolean;
    }>
  ): Observable<{ ids: Array<number> }> {
    return this.http.put<{ ids: Array<number> }>(
      this.baseURLv2 + "update-user-filter",
      { filters }
    ).pipe(
      withErrorHandler({ operation: 'updateUserFilter', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateUserFilter failed:', err);
        return throwError(() => err);
      })
    );
  }

  updateFavoriteSites(favorite_sites: Array<number | string>): Observable<any> {
    return this.http.put<any>(
      this.baseURLv2 + "favorite-site", { favorite_sites }
    ).pipe(
      withErrorHandler({ operation: 'updateFavoriteSites', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateFavoriteSites failed:', err);
        return throwError(() => err);
      })
    );
  }

  deleteUserFilter(filter_ids: Array<number>): Observable<any> {
    return this.http.delete<Filter>(
      this.baseURLv2 + "delete-user-filter", { body: { filter_ids } }
    ).pipe(
      withErrorHandler({ operation: 'deleteUserFilter', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('deleteUserFilter failed:', err);
        return throwError(() => err);
      })
    );
  }

  deleteUser(userId: number): Observable<boolean> {
    return this.http
      .delete(this.baseURLv2 + userId, { observe: "response" })
      .pipe(
        withErrorHandler({ operation: 'deleteUser', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('deleteUser failed:', err);
          return throwError(() => err);
        }),
        map((res) => {
          return res.status === 200;
        })
      );
  }

  getUserData(id: number): Observable<UserInfoData> {
    return this.http.get<UserInfoData>(this.baseURLv2 + id).pipe(
      withErrorHandler({
        operation: 'getUserData',
        cache: true,
        fallback: {} as UserInfoData,
        timeoutMs: 30000,
        retries: 2
      })
    );
  }

  updateUserData(
    id: number,
    body: {
      name: string;
      email: string;
      login: string;
      password: string;
      verified_email: boolean;
      is_ta: boolean;
      is_sa: boolean;
      enable_audit?: boolean;
    }
  ): Observable<{ ok: boolean; msg?: string }> {
    return this.http
      .post(this.baseURLv2 + id, body, {
        observe: "response",
      })
      .pipe(
        withErrorHandler({ operation: 'updateUserData', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('updateUserData failed:', err);
          return throwError(() => err);
        }),
        map((e) => {
          if (e.status == 200) {
            return { ok: true };
          } else {
            return { ok: false, msg: e.body["error"] };
          }
        })
      );
  }

  listTiers(
    only_master,
    checkFree: true
  ): Observable<{ tiers: Tier[]; freeAvailable: boolean }>;
  listTiers(only_master: any, checkFree: true): Observable<{ tiers: Tier[]; freeAvailable: boolean }>;
  listTiers(only_master: any, checkFree?: false): Observable<Tier[]>;
  listTiers(only_master: any, checkFree: boolean = false): Observable<Tier[]> | Observable<{ tiers: Tier[]; freeAvailable: boolean }> {
    if (checkFree) {
      return this.http
        .get<Tier[]>(this.baseURLv2 + "tiers", {
          params: {
            master: only_master.toString(),
          },
          observe: "response",
        })
        .pipe(
          withErrorHandler({ operation: 'listTiers', timeoutMs: 30000, retries: 2 }),
          map((r) => {
            // If fallback, r is already the correct object
            if (this.isTierListResponse(r)) {
              return r;
            }
            return {
              tiers: (r as any).body as Tier[],
              freeAvailable: (r as any).headers.get("X-Free-Tier-Available") === "true",
            };
          }),
          withErrorHandler({
            operation: 'listTiersWithFreeFlag',
            cache: true,
            fallback: { tiers: [], freeAvailable: false },
            timeoutMs: 30000,
            retries: 2
          })
        );
    } else {
      return this.http.get<Tier[]>(this.baseURLv2 + "tiers", {
        params: {
          master: only_master.toString(),
        },
      }).pipe(
        withErrorHandler({
          operation: 'listTiersBasic',
          cache: true,
          fallback: [] as Tier[],
          timeoutMs: 30000,
          retries: 2
        })
      ) as Observable<Tier[]>;
    }
  }

  setDefaultTier(id:number): Observable<any> {
    return this.http.put(this.baseURLv2 + "default-tier/" + id, {}).pipe(
      withErrorHandler({ operation: 'setDefaultTier', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('setDefaultTier failed:', err);
        return throwError(() => err);
      })
    );
  }

  getProfile(ids: Array<number>, isAdmin: boolean, includeSites: true): Observable<{ profile: Record<string, Profile>; available_sites: any[] }>;
  getProfile(ids: Array<number>, isAdmin?: boolean, includeSites?: false): Observable<Record<string, Profile>>;
  getProfile(ids: Array<number>, isAdmin: boolean = false, includeSites = false): Observable<any> {
    const params = {
      user_ids: ids ? ids.toString() : "",
      admin: isAdmin.toString(),
    };
    if (includeSites) {
      params["include_sites"] = 1;
    }
    const fallback = includeSites ? { profile: {}, available_sites: [] } : {};
    return this.http.get<any>(this.baseURLv2 + "profile", {
      params,
    }).pipe(
      withErrorHandler({
        operation: includeSites ? 'getProfileWithSites' : 'getProfile',
        cache: true,
        fallback,
        timeoutMs: 30000,
        retries: 2
      })
    );
  }

  putProfile(profile: Profile, id: number) {
    return this.http.put(this.baseURLv2 + "profile", profile, {
      params: {
        user_id: id ? id.toString() : "",
      },
    }).pipe(
      withErrorHandler({ operation: 'putProfile', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('putProfile failed:', err);
        return throwError(() => err);
      })
    );
  }

  sendValidation(): Observable<{ ok: boolean; msg?: string }> {
    return this.http
      .post(
        this.baseURLv2 + "send_validation",
        {},
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({ operation: 'sendValidation', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('sendValidation failed:', err);
          return throwError(() => err);
        }),
        map((e) => {
          if (e.status === 200) {
            return { ok: true };
          } else {
            return { ok: false, msg: e.body["error"] };
          }
        })
      );
  }

  getUsers(body): Observable<any> {
    return this.http.post<any>(this.baseURLmisc + "search-users", body).pipe(
      withErrorHandler({
        operation: 'getUsers',
        cache: true,
        fallback: [],
        timeoutMs: 30000,
        retries: 2
      })
    );
  }

  getCustomSites(id: number): Observable<any> {
    return this.http.get<any>(this.baseURLv2 + "custom-site/" + id).pipe(
      withErrorHandler({
        operation: 'getCustomSites',
        cache: true,
        fallback: [],
        timeoutMs: 30000,
        retries: 2
      })
    );
  }

  setCustomSites(id: number, sites: Array<number>): Observable<any> {
    return this.http.put<any>(this.baseURLv2 + "custom-site", {
      id: id,
      enabled_sites: sites,
    }).pipe(
      withErrorHandler({ operation: 'setCustomSites', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('setCustomSites failed:', err);
        return throwError(() => err);
      })
    );
  }

  joinFreeTiers() {
    return this.http
      .post(this.baseURLv2 + "join-free", null)
      .pipe(
        withErrorHandler({ operation: 'joinFreeTiers', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('joinFreeTiers failed:', err);
          return throwError(() => err);
        })
      )
      .toPromise();
  }

  /**
   * User admin. Enqueues Sharkscope tier-owner audit (background job, 30 min server timeout).
   * Returns `{ job_id, kind }`; poll {@link getBackgroundJobStatus} with the same kind.
   */
  startTierOwnerAuditJob(
    ownerUserId: number,
    start: string,
    end: string,
    skipGroup: boolean
  ): Observable<HttpResponse<{ job_id: string; kind: string }>> {
    let params = new HttpParams().set("start", start).set("end", end);
    if (skipGroup) {
      params = params.set("skip_group", "true");
    }
    return this.http.post<{ job_id: string; kind: string }>(
      `${this.baseURLmisc}run-audit-for-tier-owner/${ownerUserId}`,
      null,
      { params, observe: "response" }
    );
  }

  getBackgroundJobStatus(kind: string, jobId: string): Observable<BackgroundJobStatus> {
    return this.http.get<BackgroundJobStatus>(
      `${this.baseURLmisc}background-job/${encodeURIComponent(kind)}/${encodeURIComponent(jobId)}`
    );
  }
}
